import config from "config";
import { useViaduckEnabled } from "duck/api/hooks";
import { LDFlagSet } from "launchdarkly-js-client-sdk";
import { useTranslation } from "react-i18next";
import { generatePath } from "react-router";
import AirportShuttleOutlinedIcon from "@mui/icons-material/AirportShuttleOutlined";
import AppRegistrationOutlinedIcon from "@mui/icons-material/AppRegistrationOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import BookOutlinedIcon from "@mui/icons-material/BookOutlined";
import BuildCircleOutlinedIcon from "@mui/icons-material/BuildCircleOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import DirectionsCarOutlinedIcon from "@mui/icons-material/DirectionsCarOutlined";
import DisplaySettingsOutlinedIcon from "@mui/icons-material/DisplaySettingsOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import FlashlightOnOutlinedIcon from "@mui/icons-material/FlashlightOnOutlined";
import FormatColorTextOutlinedIcon from "@mui/icons-material/FormatColorTextOutlined";
import GppBadOutlinedIcon from "@mui/icons-material/GppBadOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import InsertPhotoOutlinedIcon from "@mui/icons-material/InsertPhotoOutlined";
import LightbulbOutlineRoundedIcon from "@mui/icons-material/LightbulbOutlineRounded";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import ListOutlinedIcon from "@mui/icons-material/ListOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import PlagiarismOutlinedIcon from "@mui/icons-material/PlagiarismOutlined";
import PlumbingOutlinedIcon from "@mui/icons-material/PlumbingOutlined";
import PowerOutlinedIcon from "@mui/icons-material/PowerOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import SensorsOutlinedIcon from "@mui/icons-material/SensorsOutlined";
import SettingsSuggestOutlinedIcon from "@mui/icons-material/SettingsSuggestOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import TireRepairOutlinedIcon from "@mui/icons-material/TireRepairOutlined";
import TrackChangesOutlinedIcon from "@mui/icons-material/TrackChangesOutlined";
import TranslateOutlinedIcon from "@mui/icons-material/TranslateOutlined";

import { useSchemaDefinitionList } from "shared/api/schemaDefinitions/hooks";
import { TableTypeEnum } from "shared/api/schemaDefinitions/types";
import {
  getSidebarSchemas,
  getVisibleDashboardViews,
} from "shared/api/schemaDefinitions/utils";
import { useConfigContext } from "shared/contexts/ConfigContext";
import { useTenantServiceRecordName } from "shared/hooks";
import SchemaIcon from "shared/schemaDefinitions/SchemaIcon";
import { EventTypeEnum } from "shared/types";

import { getSchemaHomeRoute } from "pages/shared/schemaDefinitions/utils";
import { canUseConnections } from "pages/SuperAdmin/Connections/utils";
import {
  AI_KNOWLEDGE_PAGE_CONFIG_KEY,
  ASSETS_PAGE_CONFIG_KEY,
  CLAIM_ANALYTICS_PAGE_CONFIG_KEY,
  CUSTOM_SIGNAL_EVENTS_PAGE_CONFIG_KEY,
  DATA_TABLES_PAGE_CONFIG_KEY,
  DIRECTIVES_PAGE_CONFIG_KEY,
  DUCK2SIEMS_JOB_STATUS_PAGE_CONFIG_KEY,
  EXTERNAL_LINKS_PAGE_CONFIG_KEY,
  FAILURE_MODES_PAGE_CONFIG_KEY,
  FLEETS_PAGE_CONFIG_KEY,
  GROUPS_PAGE_CONFIG_KEY,
  INSPECTION_ANALYTICS_PAGE_CONFIG_KEY,
  ISSUES_PAGE_CONFIG_KEY,
  KPI_TARGETS_PAGE_CONFIG_KEY,
  PagesConfigModel,
  REPAIR_ANALYTICS_PAGE_CONFIG_KEY,
  REPAIR_LABOR_PAGE_CONFIG_KEY,
  REPAIR_PARTS_PAGE_CONFIG_KEY,
  REPAIRS_PAGE_CONFIG_KEY,
  SCHEDULES_PAGE_CONFIG_KEY,
  SERVICE_RECORDS_PAGE_CONFIG_KEY,
  SIGNAL_EVENTS_ANALYTICS_PAGE_CONFIG_KEY,
  TIRE_SETTINGS_PAGE_CONFIG_KEY,
  VEHICLES_PAGE_CONFIG_KEY,
  WORK_ORDERS_PAGE_CONFIG_KEY,
  WORK_ORDERS_VERIFICATION_PAGE_CONFIG_KEY,
} from "pages/SuperAdmin/PagesConfig/types";
import {
  getExternalLinks,
  isVisibleExternalLink,
} from "pages/SuperAdmin/PagesConfig/utils";

import TenantIcon from "features/layout/TenantIcon";
import DatabaseIcon from "features/ui/Icons/DatabaseIcon";
import DirectivesIcon from "features/ui/Icons/DirectivesIcon";
import ExternalLinkIcon from "features/ui/Icons/ExternalLinkIcon";

import { routes } from "services/routes";

import {
  NUMBER_OF_SCHEMA_DEFINITIONS_TO_LOAD,
  SIDEBAR_ICON_SIZE,
} from "./constants";
import { NavGroup, NavItem, NavItemOrNothing, Props } from "./types";

const useQualityNavItems = ({
  pages,
  flags,
}: {
  pages: PagesConfigModel;
  flags: LDFlagSet;
}): NavItemOrNothing[] => {
  const { t } = useTranslation("pages");
  const { data: allSchemas } = useSchemaDefinitionList({
    limit: NUMBER_OF_SCHEMA_DEFINITIONS_TO_LOAD,
  });

  const qualityItems: NavItemOrNothing[] = [
    pages[SIGNAL_EVENTS_ANALYTICS_PAGE_CONFIG_KEY] &&
      flags.signalEventsAnalytics && {
        icon: SensorsOutlinedIcon,
        text: t("signal_event_analytics.title"),
        to: routes.signalEventAnalytics,
      },
    pages[CLAIM_ANALYTICS_PAGE_CONFIG_KEY] &&
      flags.claimAnalytics && {
        icon: AssignmentOutlinedIcon,
        text: t("claim_analytics.title"),
        to: routes.claimAnalytics,
      },
  ];

  if (pages.landingPage?.views && pages.landingPage.views.length) {
    pages.landingPage?.views.forEach((view) => {
      if (
        view.eventType === EventTypeEnum.SIGNAL_EVENT &&
        view.shouldCreateRoute &&
        flags.signalEventsAnalytics
      ) {
        qualityItems.push({
          icon: view.iconSvg ? (
            <TenantIcon svg={view.iconSvg} />
          ) : (
            SensorsOutlinedIcon
          ),
          text: view.title,
          to: generatePath(routes.signalEventAnalyticsView, {
            viewTitle: view.title,
          }),
        });
      } else if (
        view.eventType === EventTypeEnum.CLAIM &&
        view.shouldCreateRoute &&
        flags.claimAnalytics
      ) {
        qualityItems.push({
          icon:
            view.iconSvg && view.iconSvg.length > 0 ? (
              <TenantIcon svg={view.iconSvg} />
            ) : (
              AssignmentOutlinedIcon
            ),
          text: view.title,
          to: generatePath(routes.claimAnalyticsView, {
            viewTitle: view.title,
          }),
          hasUnlistedSubroutes: true,
        });
      }
    });
  }

  // Adding Dynamic Model/Schema pages

  getSidebarSchemas(allSchemas ?? []).forEach((s) => {
    qualityItems.push({
      icon: (
        <SchemaIcon
          tableType={s.tableType}
          config={s.config}
          size={SIDEBAR_ICON_SIZE}
        />
      ),
      text: s.displayNamePlural,
      to: getSchemaHomeRoute(s),
    });
  });

  return qualityItems.filter(Boolean);
};

// dynamic schema dashboard
export const useSchemaDashboardNavItem = (): NavItem | undefined => {
  const { t } = useTranslation("pages");
  const { userInfo } = useConfigContext();
  const { data: allSchemas } = useSchemaDefinitionList({
    limit: NUMBER_OF_SCHEMA_DEFINITIONS_TO_LOAD,
    skipRequest: config.isInstanceManagement(),
  });

  const hasSchemaDashboardViews = (allSchemas ?? []).some(
    (s) =>
      s.tableType === TableTypeEnum.EVENT &&
      getVisibleDashboardViews(s, userInfo.isSuperAdmin).length > 0
  );

  if (!hasSchemaDashboardViews) return undefined;

  return {
    icon: SpeedOutlinedIcon,
    text: t("dashboard.title"),
    to: routes.schemaDashboard,
  };
};

export const useTopLevelNavItems = ({ flags }: Props): NavItem[] => {
  const { t } = useTranslation("pages");
  const { pages } = useConfigContext();

  const qualityAnalyticsItems = useQualityNavItems({ pages, flags });

  const quality = qualityAnalyticsItems.filter(Boolean) as NavItem[];

  const tenantServiceRecordName = useTenantServiceRecordName();

  const serviceItems: NavItemOrNothing[] = [
    pages[ISSUES_PAGE_CONFIG_KEY] &&
      flags.detectedIssues && {
        icon: ErrorOutlineRoundedIcon,
        text: t("issues.title"),
        to: routes.issues,
      },
    pages[FLEETS_PAGE_CONFIG_KEY] && {
      icon: LocalShippingOutlinedIcon,
      text: t("fleets.title"),
      to: routes.fleets,
      hasUnlistedSubroutes: true,
    },
    pages[VEHICLES_PAGE_CONFIG_KEY] && {
      icon: DirectionsCarOutlinedIcon,
      text: t("vehicles.title"),
      to: routes.vehicles,
      hasUnlistedSubroutes: true,
      testId: "nav-vehicles",
    },
    pages[ASSETS_PAGE_CONFIG_KEY] && {
      icon: AirportShuttleOutlinedIcon,
      text: t("assets.title"),
      to: routes.assets,
      hasUnlistedSubroutes: true,
    },
    pages[SERVICE_RECORDS_PAGE_CONFIG_KEY] &&
      !pages[SERVICE_RECORDS_PAGE_CONFIG_KEY].hideMenuItem && {
        icon: ListAltOutlinedIcon,
        text: tenantServiceRecordName,
        to: routes.serviceRecords,
      },
    pages[INSPECTION_ANALYTICS_PAGE_CONFIG_KEY] &&
      flags.inspectionAnalytics && {
        icon: FlashlightOnOutlinedIcon,
        text: t("inspection_analytics.title"),
        to: routes.inspectionAnalytics,
        hasUnlistedSubroutes: true,
      },
    pages[REPAIR_ANALYTICS_PAGE_CONFIG_KEY] &&
      flags.repairAnalytics && {
        icon: BuildOutlinedIcon,
        text: t("repair_analytics.title"),
        to: routes.repairAnalytics,
        hasUnlistedSubroutes: true,
      },
    pages[REPAIRS_PAGE_CONFIG_KEY] && {
      icon: BuildOutlinedIcon,
      text: t("repairs.title"),
      to: routes.repairs,
      hasUnlistedSubroutes: true,
    },
    pages[WORK_ORDERS_PAGE_CONFIG_KEY] && {
      icon: FactCheckOutlinedIcon,
      text: t("work_orders.title"),
      to: routes.workOrders,
      hasUnlistedSubroutes: true,
    },
    pages[WORK_ORDERS_VERIFICATION_PAGE_CONFIG_KEY] && {
      icon: FactCheckOutlinedIcon,
      text: t("work_order_verifications.title"),
      to: routes.workOrdersVerification,
      hasUnlistedSubroutes: true,
    },

    pages[REPAIR_PARTS_PAGE_CONFIG_KEY] && {
      icon: SettingsSuggestOutlinedIcon,
      text: t("parts.title"),
      to: routes.repairParts,
    },
    pages[REPAIR_LABOR_PAGE_CONFIG_KEY] && {
      icon: PlumbingOutlinedIcon,
      text: t("labor.title"),
      to: routes.repairLabor,
    },
    pages[FAILURE_MODES_PAGE_CONFIG_KEY] && {
      icon: GppBadOutlinedIcon,
      text: t("failure_modes.title"),
      to: routes.failureModes,
      hasUnlistedSubroutes: true,
    },
    pages[TIRE_SETTINGS_PAGE_CONFIG_KEY] && {
      icon: TireRepairOutlinedIcon,
      text: t("tire_settings.title"),
      to: routes.tireSettings,
    },
    pages[KPI_TARGETS_PAGE_CONFIG_KEY] && {
      icon: TrackChangesOutlinedIcon,
      text: t("kpi_targets.title"),
      to: routes.kpiTargets,
    },
    pages[DUCK2SIEMS_JOB_STATUS_PAGE_CONFIG_KEY] && {
      icon: PendingActionsOutlinedIcon,
      text: t("pages:siems_job_status.title"),
      to: routes.duck2SiemsJobStatus,
    },
  ];

  const service = serviceItems.filter(Boolean) as NavItem[];

  const externalLinks: NavItem[] = getExternalLinks(
    pages[EXTERNAL_LINKS_PAGE_CONFIG_KEY]?.links
  )
    .filter(isVisibleExternalLink)
    .map(({ id, title, url, iconSvg }) => ({
      icon: <ExternalLinkIcon svg={iconSvg} size={SIDEBAR_ICON_SIZE} />,
      text: title,
      to: url,
      isExternal: true,
      testId: `nav-external-link-${id}`,
    }));

  return [...quality, ...service, ...externalLinks];
};

export const useSideNavGroups = ({ flags }: Props): NavGroup[] => {
  const { t } = useTranslation(["pages", "common"]);
  const { pages, general, userInfo } = useConfigContext();
  const duckEnabled = useViaduckEnabled();

  const { dataExplorer } = config.get();

  const dataHubItems: NavItemOrNothing[] = [
    pages[CUSTOM_SIGNAL_EVENTS_PAGE_CONFIG_KEY] &&
      flags.customSignalEvents && {
        icon: AssignmentOutlinedIcon,
        text: t("custom_signal_events.title"),
        to: routes.customSignalEvents,
        hasUnlistedSubroutes: true,
      },
    pages.dataExplorer && {
      icon: PlagiarismOutlinedIcon,
      text: t("data_explorer.title"),
      isExternal: true,
      to: dataExplorer?.url,
      testId: "nav-data-explorer",
    },
    pages[DATA_TABLES_PAGE_CONFIG_KEY] && {
      icon: DatabaseIcon,
      text: t("pages:data.title"),
      to: routes.data,
      hasUnlistedSubroutes: true,
      testId: "nav-data",
    },
    canUseConnections({ pages, flags }) && {
      icon: PowerOutlinedIcon,
      text: t("pages:connections.title"),
      to: routes.connections,
      hasUnlistedSubroutes: true,
      testId: "nav-connections",
    },
    pages[GROUPS_PAGE_CONFIG_KEY] && {
      icon: GroupsOutlinedIcon,
      text: t("groups.title"),
      to: routes.groups,
      hasUnlistedSubroutes: true,
    },
    pages[DIRECTIVES_PAGE_CONFIG_KEY] && {
      icon: DirectivesIcon,
      text: t("directives.title"),
      to: routes.directives,
      hasUnlistedSubroutes: true,
      testId: "nav-directives",
    },
    pages[SCHEDULES_PAGE_CONFIG_KEY] && {
      icon: ScheduleOutlinedIcon,
      text: t("pages:schedules.title"),
      to: routes.schedules,
    },
    pages[AI_KNOWLEDGE_PAGE_CONFIG_KEY] && {
      icon: AutoAwesomeOutlinedIcon,
      text: t("ai_knowledge.title"),
      to: routes.aiKnowledge,
    },
  ];

  const dataHub = dataHubItems.filter(Boolean) as NavItem[];
  const dashboardsItems: NavItemOrNothing[] = general?.metabaseDashboards
    ? general.metabaseDashboards.map(({ id, name }) => ({
        icon: SpeedOutlinedIcon,
        text: <span>{name}</span>,
        to: generatePath(routes.dashboard, {
          id: String(id),
        }),
        testId: `nav-dashboard-${id}`,
      }))
    : [];

  const DASHBOARDS = dashboardsItems.filter(Boolean) as NavItem[];

  const agenticCodingPages: NavItem[] = [
    {
      icon: SmartToyOutlinedIcon,
      text: t("pages:agentic_coding.tools.title"),
      to: routes.agenticTools,
    },
  ];

  const adminPages: NavItem[] = [
    {
      icon: GroupsOutlinedIcon,
      text: t("admin_groups.title"),
      to: routes.adminGroups,
    },
    {
      icon: PersonOutlineOutlinedIcon,
      text: t("users.title"),
      to: routes.users,
    },
  ];

  const superAdminPages: NavItem[] = [
    {
      icon: GroupsOutlinedIcon,
      text: t("groups.super_admin_title"),
      to: routes.superAdminGroups,
    },
    {
      icon: ListOutlinedIcon,
      text: t("attributes_config.title"),
      to: routes.attributesConfig,
    },
    {
      icon: AppRegistrationOutlinedIcon,
      text: t("pages_config.title"),
      to: routes.pagesConfig,
    },
    {
      icon: DashboardOutlinedIcon,
      text: t("dashboard_config.title"),
      to: routes.dashboardConfig,
    },
    {
      icon: DisplaySettingsOutlinedIcon,
      text: t("general_config.title"),
      to: routes.generalConfig,
    },
    {
      icon: FormatColorTextOutlinedIcon,
      text: t("branding_config.title"),
      to: routes.brandingConfig,
    },
    duckEnabled && {
      icon: LightbulbOutlineRoundedIcon,
      text: t("duck_config.title"),
      to: routes.duckConfig,
    },
    duckEnabled && {
      icon: BuildCircleOutlinedIcon,
      text: t("duck_chat_tools.title"),
      to: routes.duckChatTools,
    },
    {
      icon: InsertPhotoOutlinedIcon,
      text: t("vehicle_images.title"),
      to: routes.vehicleImages,
    },
    {
      icon: TranslateOutlinedIcon,
      text: t("localization.title"),
      to: routes.localization,
    },
    {
      icon: InfoOutlinedIcon,
      text: t("info.title"),
      to: routes.info,
    },
    {
      icon: BookOutlinedIcon,
      text: t("knowledge_base.title"),
      to: routes.knowledgeRepoManagement,
    },
    {
      icon: StorageOutlinedIcon,
      text: t("data_sources.title"),
      to: routes.schemaDefinitions,
      hasUnlistedSubroutes: true,
    },
    {
      icon: EmailOutlinedIcon,
      text: t("email_notifications.title"),
      to: routes.emailNotifications,
    },
    {
      icon: MarkEmailReadOutlinedIcon,
      text: t("email_pipelines.title"),
      to: routes.emailPipelineWorkflows,
      hasUnlistedSubroutes: true,
    },
  ].filter(Boolean) as NavItem[];

  const filterSideBarSections = ({ links }: NavGroup) => links.length > 0;

  const groupItems: NavGroup[] = [
    {
      title: t("common:menu.section.dashboards"),
      links: DASHBOARDS,
    },
    {
      title: t("common:menu.section.data_hub"),
      links: dataHub,
    },
    flags.agenticCoding && {
      title: t("pages:agentic_coding.title"),
      links: agenticCodingPages,
    },
    (userInfo.isAdmin || userInfo.isSuperAdmin) && {
      title: t("common:menu.section.admin"),
      links: adminPages,
    },
    userInfo.isSuperAdmin && {
      title: t("common:menu.section.super_admin"),
      links: superAdminPages,
    },
  ].filter(Boolean) as NavGroup[];

  return groupItems.filter(Boolean).filter(filterSideBarSections) as NavGroup[];
};
